/**
 * BioRevive 2026 — outbound clicks through the store's existing Google tag.
 *
 * Sends one `outbound_click`, with retailer details when applicable.
 * Requires Shopify analytics consent and an existing window.gtag.
 * Never loads/configures Google tags or changes their consent settings.
 *
 * Optional data attributes on any link override the inferred values:
 *   data-retailer-name, data-click-location, data-product-id,
 *   data-product-title, data-product-handle
 */
(function () {
  'use strict';

  var config = (window.BR && window.BR.tracking) || {};
  if (config.outbound === false || config.designMode || !/^G-[A-Z0-9]+$/.test(config.ga4Id || '') || window.__brOutboundTrackingStarted) {
    return;
  }
  window.__brOutboundTrackingStarted = true;

  function analyticsProcessingAllowed() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    return Boolean(
      privacy &&
        typeof privacy.analyticsProcessingAllowed === 'function' &&
        privacy.analyticsProcessingAllowed()
    );
  }

  // Read consent at click time, including grants/revocations after page load.
  if (
    window.Shopify &&
    !window.Shopify.customerPrivacy &&
    typeof window.Shopify.loadFeatures === 'function'
  ) {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function () {} // If loading fails, clicks stay untracked.
    );
  }

  var RETAILERS = {
    'chemistwarehouse.com.au': 'Chemist Warehouse',
    'chemistwarehouse.co.nz': 'Chemist Warehouse',
    'priceline.com.au': 'Priceline',
    'terrywhitechemmart.com.au': 'TerryWhite Chemmart',
    'amcal.com.au': 'Amcal',
    'discountdrugstores.com.au': 'Discount Drug Stores',
    'blooms.net.au': 'Blooms The Chemist',
    'pharmacy4less.com.au': 'Pharmacy 4 Less',
    'goodpricepharmacy.com.au': 'Good Price Pharmacy',
    'mychemist.com.au': 'My Chemist',
    'pharmacyonline.com.au': 'Pharmacy Online',
    'amazon.com.au': 'Amazon',
    'woolworths.com.au': 'Woolworths',
    'coles.com.au': 'Coles'
  };

  var SOCIAL = [
    'facebook.com',
    'instagram.com',
    'tiktok.com',
    'youtube.com',
    'youtu.be',
    'x.com',
    'twitter.com',
    'pinterest.com',
    'linkedin.com',
    'snapchat.com',
    'tumblr.com',
    'vimeo.com'
  ];

  var RETAILER_LINK_SELECTOR = 'a.br-cw-buy__btn, a.chemist-warehouse-btn, a[data-retailer-name]';

  function normaliseHost(host) {
    return String(host || '')
      .toLowerCase()
      .replace(/^www\./, '');
  }

  function hostMatches(host, domain) {
    return host === domain || host.slice(-(domain.length + 1)) === '.' + domain;
  }

  var internalHosts = [window.location.hostname, config.shopHost, config.permanentDomain, window.shopUrl]
    .map(function (value) {
      if (!value) return '';
      try {
        return normaliseHost(/^https?:\/\//i.test(value) ? new URL(value).hostname : value);
      } catch (error) {
        return '';
      }
    })
    .filter(Boolean);

  function isInternalHost(host) {
    return internalHosts.some(function (internal) {
      return hostMatches(host, internal);
    });
  }

  function findRetailer(host) {
    var domains = Object.keys(RETAILERS);
    for (var i = 0; i < domains.length; i += 1) {
      if (hostMatches(host, domains[i])) {
        return RETAILERS[domains[i]];
      }
    }
    return null;
  }

  function isSocial(host) {
    return SOCIAL.some(function (domain) {
      return hostMatches(host, domain);
    });
  }

  function getOutboundLink(target) {
    if (!target || typeof target.closest !== 'function') {
      return null;
    }

    var link = target.closest('a[href]');
    if (!link) {
      return null;
    }

    var protocol = (link.protocol || '').toLowerCase();
    if (protocol !== 'http:' && protocol !== 'https:') {
      return null;
    }

    var host = normaliseHost(link.hostname);
    if (!host || isInternalHost(host)) {
      return null;
    }

    return link;
  }

  function getSelectedVariant(productInfo) {
    var variantScript = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (!variantScript) {
      return {};
    }

    try {
      var variant = JSON.parse(variantScript.textContent);
      if (!variant || !variant.id) return {};
      return {
        variant_id: String(variant.id),
        variant_sku: variant.sku || ''
      };
    } catch (error) {
      return {};
    }
  }

  function getProductContext(link) {
    var productInfo = link.closest('product-info[data-product-id]');
    var productHolder = link.closest('[data-product-id]');
    var handleFromUrl = productInfo && productInfo.dataset.url ? productInfo.dataset.url.split('?')[0].split('/').filter(Boolean).pop() : '';
    var titleEl = productInfo && productInfo.querySelector('.product__title');
    var titleFromPage = titleEl ? titleEl.textContent.trim() : '';

    var context = {
      product_id: link.dataset.productId || (productHolder && productHolder.dataset.productId) || (productInfo && productInfo.dataset.productId),
      product_title: link.dataset.productTitle || (productInfo ? titleFromPage : ''),
      product_handle: link.dataset.productHandle || handleFromUrl
    };

    Object.keys(context).forEach(function (key) {
      if (!context[key]) {
        delete context[key];
      }
    });

    if (productInfo) {
      Object.assign(context, getSelectedVariant(productInfo));
    }

    return context;
  }

  function inferClickLocation(link) {
    if (link.dataset.clickLocation) {
      return link.dataset.clickLocation;
    }
    if (link.classList.contains('br-cw-buy__btn')) {
      return 'pdp_primary';
    }
    if (link.closest('.product__description, .rte')) {
      return link.closest('product-info') ? 'product_description' : 'rich_text';
    }
    if (link.closest('.socials, .social__link')) {
      return 'social_icons';
    }
    if (link.closest('header, .header, #shopify-section-header, .shopify-section-group-header-group')) {
      return 'header';
    }
    if (link.closest('footer, .footer, .shopify-section-group-footer-group')) {
      return 'footer';
    }
    if (link.closest('product-info')) {
      return 'pdp';
    }

    var section = link.closest('.shopify-section[id]');
    if (section) {
      var sectionType = section.getAttribute('data-section-type');
      if (sectionType) {
        return sectionType;
      }
      var parts = section.id.replace(/^shopify-section-/, '').split('__');
      return parts[parts.length - 1] || section.id;
    }

    return 'page';
  }

  function classifyLink(link) {
    var host = normaliseHost(link.hostname);
    var retailer = link.dataset.retailerName || findRetailer(host);

    if (!retailer && (link.matches(RETAILER_LINK_SELECTOR) || link.querySelector('.chemist-warehouse-img'))) {
      retailer = 'Chemist Warehouse';
    }

    if (retailer) {
      return { link_type: 'retailer', retailer: retailer };
    }
    if (isSocial(host)) {
      return { link_type: 'social' };
    }
    return { link_type: 'other' };
  }

  function buildPayload(link) {
    var img = link.querySelector('img[alt]');
    var text = (link.getAttribute('aria-label') || link.textContent.trim() || (img && img.alt) || link.title || '')
      .replace(/\s+/g, ' ')
      .trim();
    var payload = {
      outbound: true,
      link_url: link.href,
      link_domain: normaliseHost(link.hostname),
      link_text: text.slice(0, 100),
      link_id: link.id || '',
      link_classes: (link.className && typeof link.className === 'string' ? link.className : '').slice(0, 100),
      click_location: inferClickLocation(link),
      page_path: window.location.pathname
    };

    Object.assign(payload, classifyLink(link));

    if (payload.link_type === 'retailer') {
      Object.assign(payload, getProductContext(link));
    }

    return payload;
  }

  function onClick(event) {
    if (event.type === 'auxclick' ? event.button !== 1 : event.button !== 0) return;
    var link = getOutboundLink(event.target);
    if (link && typeof window.gtag === 'function' && analyticsProcessingAllowed()) {
      window.gtag('event', 'outbound_click', Object.assign({ send_to: config.ga4Id }, buildPayload(link)));
    }
  }

  document.addEventListener('click', onClick, { capture: true });
  document.addEventListener('auxclick', onClick, { capture: true });
})();
